/**
 * AgentTable - Generic data table for agent visualizations
 * Enhanced with editable cell support for bank statement import
 */

import React, { useState, useMemo, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table';
import { Button } from '../../ui/button';
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { TableVisualization, formatValue, formatDate, FormatType } from './types';
import { LedgerAutocomplete } from './LedgerAutocomplete';
import { useChatStore } from '../../../stores/chat/store';

interface AgentTableProps {
  data: TableVisualization;
  className?: string;
}

type SortDirection = 'asc' | 'desc' | null;

export const AgentTable: React.FC<AgentTableProps> = ({ data, className }) => {
  const { title, columns, format_columns, data: tableData, table_id, editable_columns, select_options, highlight_rows } = data;
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);

  // Editable table state
  const [editedData, setEditedData] = useState<Record<string, any>[]>(tableData);
  const isEditable = editable_columns && editable_columns.length > 0;

  // WebSocket for sending confirmations
  const ws = useChatStore(state => state.ws);

  // Reset edited data when table data changes
  useEffect(() => {
    setEditedData(tableData);
  }, [tableData]);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortColumn(null);
        setSortDirection(null);
      }
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Handle cell edits
  const handleCellEdit = (rowIndex: number, column: string, newValue: string) => {
    setEditedData(prev => {
      const updated = [...prev];
      updated[rowIndex] = { ...updated[rowIndex], [column]: newValue };
      return updated;
    });
  };

  // Handle confirm button - send to backend via WebSocket
  const handleConfirm = () => {
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      console.error('WebSocket not connected');
      return;
    }

    if (!table_id) {
      console.error('No table_id provided');
      return;
    }

    // Send table_confirm message
    ws.send(JSON.stringify({
      type: 'table_confirm',
      table_id: table_id,
      data: editedData
    }));

    console.log('[AgentTable] Sent table_confirm:', { table_id, data: editedData });
  };

  // Handle reset button
  const handleReset = () => {
    setEditedData(tableData);
  };

  // Get row highlight color based on rules
  const getRowHighlight = (row: Record<string, any>): string | undefined => {
    if (!highlight_rows) return undefined;

    for (const rule of highlight_rows) {
      if (row[rule.column] === rule.value) {
        switch (rule.color) {
          case 'red': return 'bg-red-50 dark:bg-red-950/20';
          case 'yellow': return 'bg-yellow-50 dark:bg-yellow-950/20';
          case 'green': return 'bg-green-50 dark:bg-green-950/20';
        }
      }
    }
    return undefined;
  };

  const sortedData = useMemo(() => {
    // Use editedData if editable, otherwise use original tableData
    const dataToSort = isEditable ? editedData : tableData;

    if (!sortColumn || !sortDirection) return dataToSort;

    return [...dataToSort].sort((a, b) => {
      const aVal = a[sortColumn];
      const bVal = b[sortColumn];

      if (aVal === bVal) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      const comparison = aVal < bVal ? -1 : 1;
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [editedData, tableData, sortColumn, sortDirection, isEditable]);

  const formatCellValue = (value: any, column: string): string => {
    if (value == null) return '-';

    const format = format_columns?.[column];
    if (format === 'date') {
      return formatDate(value);
    }
    if (format && typeof value === 'number') {
      return formatValue(value, format);
    }
    return String(value);
  };

  const getSortIcon = (column: string) => {
    if (sortColumn !== column) {
      return <ArrowUpDown className="h-3 w-3 ml-1" />;
    }
    return sortDirection === 'asc'
      ? <ArrowUp className="h-3 w-3 ml-1" />
      : <ArrowDown className="h-3 w-3 ml-1" />;
  };

  // Format column headers (convert snake_case to Title Case)
  const formatHeader = (column: string): string => {
    return column
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <div className={cn("w-full", className)}>
      {title && (
        <h4 className="text-sm font-medium mb-3 text-foreground">{title}</h4>
      )}
      <div className="border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                {columns.map((column) => (
                  <TableHead key={column} className="text-xs font-medium">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-auto p-0 font-medium hover:bg-transparent"
                      onClick={() => handleSort(column)}
                    >
                      {formatHeader(column)}
                      {getSortIcon(column)}
                    </Button>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedData.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="text-center text-muted-foreground py-8"
                  >
                    No data available
                  </TableCell>
                </TableRow>
              ) : (
                sortedData.map((row, rowIndex) => {
                  const highlightClass = getRowHighlight(row);
                  return (
                    <TableRow
                      key={rowIndex}
                      className={cn("hover:bg-muted/30", highlightClass)}
                    >
                      {columns.map((column) => (
                        <TableCell key={column} className="text-sm py-2">
                          {isEditable && editable_columns?.includes(column) ? (
                            // Editable cell
                            select_options?.[column] ? (
                              // Dropdown for ledger selection
                              <LedgerAutocomplete
                                value={row[column] || ''}
                                options={select_options[column]}
                                onChange={(val) => handleCellEdit(rowIndex, column, val)}
                              />
                            ) : (
                              // Text input for other editable columns
                              <input
                                type="text"
                                value={row[column] || ''}
                                onChange={(e) => handleCellEdit(rowIndex, column, e.target.value)}
                                className="w-full px-2 py-1 border rounded text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                              />
                            )
                          ) : (
                            // Read-only cell
                            formatCellValue(row[column], column)
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>
      {sortedData.length > 0 && (
        <p className="text-xs text-muted-foreground mt-2">
          {sortedData.length} {sortedData.length === 1 ? 'record' : 'records'}
        </p>
      )}

      {/* Action buttons for editable tables */}
      {isEditable && (
        <div className="flex justify-end mt-4 gap-2">
          <Button
            variant="outline"
            onClick={handleReset}
            size="sm"
          >
            Reset
          </Button>
          <Button
            onClick={handleConfirm}
            size="sm"
          >
            Confirm & Import
          </Button>
        </div>
      )}
    </div>
  );
};
